import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'
import { usePrevious } from '../../../utils'
import { NumberInputProps } from '../types'
import { parseString } from '../utils'

const numberRegex = /^[+-]?\d*\.?\d*$/

type Args = Pick<
  NumberInputProps,
  'defaultValue' | 'step' | 'precision' | 'min' | 'max' | 'onChange' | 'formattingOptions'
>

export const useNumberInput = ({
  defaultValue = 0,
  step = 1,
  precision = 0,
  min = -Infinity,
  max = Infinity,
  onChange,
  formattingOptions
}: Args) => {
  const [value, setValue] = useState<string>(
    formattingOptions ? formattingOptions.format(defaultValue.toString()) : defaultValue.toString()
  )
  const prevValue = usePrevious(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        const number = parseString(value, precision)

        if (number >= min && number <= max) {
          return handleSetValueWithFormatting(number.toString())
        }

        const minDiff = number - min
        const maxDiff = max - number

        const update = maxDiff > minDiff ? min.toString() : max.toString()
        return handleSetValueWithFormatting(update)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [inputRef, value])

  useLayoutEffect(() => {
    if (!inputRef.current) {
      return
    }

    if (inputRef.current.value !== value) {
      handleSetValue(inputRef.current.value)
    }
  }, [inputRef, value])

  useEffect(() => {
    if (onChange && prevValue !== value) {
      if (formattingOptions) {
        return onChange(formattingOptions.unFormat(value))
      }

      onChange(value)
    }
  }, [formattingOptions, onChange, value])

  const handleSetValue = (value: string) => {
    setValue(value)
  }

  const handleSetValueWithFormatting = (value: string) => {
    if (formattingOptions) {
      return setValue(formattingOptions.format(value))
    }

    setValue(value)
  }

  const increment = () => {
    const nextStep = Math.min(parseString(value, precision) + step, max)
    handleSetValueWithFormatting(nextStep.toString())
  }

  const decrement = () => {
    const prevStep = Math.max(parseString(value, precision) - step, min)
    handleSetValueWithFormatting(prevStep.toString())
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = formattingOptions
      ? formattingOptions.unFormat(event.target.value)
      : event.target.value

    if (numberRegex.test(value)) {
      handleSetValue(value)
    }
  }

  const getInputProps = ({
    ref = null,
    props
  }: {
    ref: React.RefObject<HTMLInputElement> | null
    props: React.HTMLProps<HTMLInputElement>
  }) => {
    return {
      ...props,
      ref: mergeRefs([inputRef, ref]),
      value,
      onChange: handleChange
    }
  }

  const isIncrementDisabled = parseString(value, precision) === max
  const isDecrementDisabled = parseString(value, precision) === min

  return {
    value,
    isIncrementDisabled,
    isDecrementDisabled,
    increment,
    decrement,
    getInputProps
  }
}
